/* Flytron theme — global behaviour.
   Vanilla custom elements. No jQuery, no bundler, no polyfills. */

(function () {
  'use strict';

  const routes = window.Flytron?.routes || {};
  const strings = window.Flytron?.strings || {};

  const money = (cents) => {
    const fmt = window.Flytron?.moneyFormat || '£{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const [int, dec] = amount.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, `${grouped}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, grouped)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`);
  };

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  /* ------------------------------------------------------------------ *
   * Focus trap — shared by every drawer
   * ------------------------------------------------------------------ */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------------ *
   * Announcement-bar apps
   *
   * Apps such as Hextom Quick Announcement Bar inject a fixed bar at the top
   * of the window after the theme has rendered. Nothing in the theme knows its
   * height, so measure whatever fixed element is sitting at the very top and
   * publish it as --announcement-bar-height. The sticky header offsets by that
   * value; if no such bar exists the variable stays 0 and nothing changes.
   * ------------------------------------------------------------------ */
  function measureTopBar() {
    let height = 0;

    for (const el of document.body.children) {
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      if (el.closest('.header-wrap')) continue;

      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;

      const rect = el.getBoundingClientRect();
      // Only bars actually parked against the top edge, spanning the width
      if (rect.top > 2 || rect.height === 0) continue;
      if (rect.width < window.innerWidth * 0.6) continue;
      if (rect.height > window.innerHeight * 0.4) continue;

      height = Math.max(height, Math.round(rect.bottom));
    }

    document.documentElement.style.setProperty(
      '--announcement-bar-height', height + 'px'
    );
  }

  const scheduleMeasure = () => requestAnimationFrame(measureTopBar);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleMeasure);
  } else {
    scheduleMeasure();
  }
  window.addEventListener('load', scheduleMeasure);
  window.addEventListener('resize', debounce(measureTopBar, 150));

  // App bars arrive after their script loads, so watch for late insertions.
  if ('MutationObserver' in window) {
    new MutationObserver(debounce(measureTopBar, 120))
      .observe(document.body, { childList: true, subtree: false });
  }

  /* ------------------------------------------------------------------ *
   * <drawer-element> — mobile menu, cart drawer, mobile filters
   * ------------------------------------------------------------------ */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.overlay = this.querySelector('.drawer__overlay');
      this.opener = null;

      this.overlay?.addEventListener('click', () => this.close());
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
        if (this.classList.contains('is-open')) trapFocus(this.panel, e);
      });

      // Any button pointing at this drawer's id opens it
      document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        });
      });
    }

    open(opener) {
      this.opener = opener || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      this.opener?.setAttribute('aria-expanded', 'true');

      // Wait for the transition to start before moving focus
      requestAnimationFrame(() => {
        const target =
          this.panel.querySelector('[data-drawer-focus]') ||
          this.panel.querySelector(FOCUSABLE);
        target?.focus();
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      this.opener?.setAttribute('aria-expanded', 'false');
      this.opener?.focus();
      this.opener = null;
    }
  }
  customElements.define('drawer-element', DrawerElement);

  /* ------------------------------------------------------------------ *
   * <predictive-search> — type-ahead against Shopify's suggest API
   * ------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.predictive');
      this.abort = null;
      if (!this.input || !this.results) return;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('autocomplete', 'off');

      this.input.addEventListener('input', debounce(() => this.search(), 240));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length > 1) this.search();
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.hide();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') this.move(e);
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hide();
      });
    }

    move(e) {
      const links = Array.from(this.results.querySelectorAll('a'));
      if (!links.length) return;
      e.preventDefault();
      const idx = links.indexOf(document.activeElement);
      const next =
        e.key === 'ArrowDown'
          ? (idx + 1) % links.length
          : idx <= 0
          ? links.length - 1
          : idx - 1;
      links[next].focus();
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) return this.hide();

      this.abort?.abort();
      this.abort = new AbortController();

      const url = `${routes.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(
        q
      )}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;

      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const markup = doc.querySelector('.predictive__inner');
        if (markup) {
          this.results.innerHTML = markup.innerHTML;
          this.show();
        } else {
          this.hide();
        }
      } catch (err) {
        if (err.name !== 'AbortError') this.hide();
      }
    }

    show() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    hide() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ *
   * <quantity-input>
   * ------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.qty === 'plus' ? 1 : -1;
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const value = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (value === parseInt(this.input.value, 10)) return;
          this.input.value = value;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------ *
   * <variant-selector> — swaps price, availability, image, URL, JSON-LD
   * ------------------------------------------------------------------ */
  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.productHandle = this.dataset.handle;
      this.sectionId = this.dataset.section;
      this.variants = JSON.parse(
        this.querySelector('[data-variants]')?.textContent || '[]'
      );
      this.idInput = this.querySelector('input[name="id"]');

      this.addEventListener('change', () => this.onChange());
      this.onChange({ initial: true });
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map(
        (el) => el.value
      );
    }

    findVariant() {
      const chosen = this.selectedOptions;
      // A product with only a default variant renders no option inputs, so
      // there is nothing to match against — it is always the single variant.
      if (!chosen.length) return this.variants[0];
      return this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );
    }

    onChange(opts = {}) {
      const variant = this.findVariant();
      this.markUnavailable();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;

      if (!opts.initial) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        this.updateMedia(variant);
      }

      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateSku(variant);

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
      );
    }

    /* Grey out option values that pair with no purchasable variant */
    markUnavailable() {
      const chosen = this.selectedOptions;
      this.querySelectorAll('.variant-option input[type="radio"]').forEach((input) => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        const probe = [...chosen];
        probe[idx] = input.value;
        const match = this.variants.find((v) =>
          v.options.every((opt, i) => (i === idx ? opt === input.value : opt === probe[i]))
        );
        const label = input.nextElementSibling;
        label?.classList.toggle('is-unavailable', !match || !match.available);
      });
    }

    updatePrice(variant) {
      const el = document.getElementById(`price-${this.sectionId}`);
      if (!el) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.classList.toggle('price--on-sale', !!onSale);
      const now = el.querySelector('.price__now');
      const was = el.querySelector('.price__was');
      if (now) now.textContent = money(variant.price);
      if (was) {
        was.textContent = onSale ? money(variant.compare_at_price) : '';
        was.hidden = !onSale;
      }
    }

    updateSku(variant) {
      const el = document.getElementById(`sku-${this.sectionId}`);
      if (!el) return;
      el.textContent = variant.sku || '';
      el.closest('[data-sku-wrap]')?.toggleAttribute('hidden', !variant.sku);
    }

    updateAvailability(variant) {
      const btn = document.getElementById(`add-${this.sectionId}`);
      const stock = document.getElementById(`stock-${this.sectionId}`);

      if (btn) {
        btn.disabled = !variant.available;
        const text = btn.querySelector('span');
        if (text) {
          text.textContent = variant.available
            ? strings.addToCart || 'Add to cart'
            : strings.soldOut || 'Sold out';
        }
      }

      if (stock) {
        stock.className = `product__stock product__stock--${variant.available ? 'in' : 'out'}`;
        stock.textContent = variant.available
          ? strings.inStock || 'In stock'
          : strings.outOfStock || 'Out of stock';
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const main = document.getElementById(`gallery-main-${this.sectionId}`);
      const thumb = document.querySelector(
        `[data-media-id="${variant.featured_media.id}"]`
      );
      if (thumb) {
        thumb.click();
      } else if (main) {
        const img = main.querySelector('img');
        if (img) {
          img.src = variant.featured_media.preview_image.src;
          img.srcset = '';
          img.alt = variant.featured_media.alt || '';
        }
      }
    }

    setUnavailable() {
      const btn = document.getElementById(`add-${this.sectionId}`);
      if (btn) {
        btn.disabled = true;
        const text = btn.querySelector('span');
        if (text) text.textContent = strings.unavailable || 'Unavailable';
      }
    }
  }
  customElements.define('variant-selector', VariantSelector);

  /* ------------------------------------------------------------------ *
   * <product-gallery>
   * ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main] img');
      this.querySelectorAll('[data-media-id]').forEach((btn) =>
        btn.addEventListener('click', () => this.select(btn))
      );
    }

    select(btn) {
      if (!this.main) return;
      this.main.src = btn.dataset.full;
      this.main.srcset = btn.dataset.srcset || '';
      this.main.alt = btn.querySelector('img')?.alt || '';
      this.querySelectorAll('[data-media-id]').forEach((b) =>
        b.setAttribute('aria-current', String(b === btn))
      );
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ *
   * <cart-form> — add to cart without a page load
   * ------------------------------------------------------------------ */
  class CartForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.errors = this.querySelector('[data-cart-error]');
      this.form?.addEventListener('submit', (e) => this.submit(e));
    }

    async submit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled) return;

      this.setBusy(true);
      if (this.errors) this.errors.hidden = true;

      try {
        const body = new FormData(this.form);
        body.append('sections', 'cart-drawer,header-cart-count');

        const res = await fetch(routes.cart_add_url || '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const data = await res.json();

        if (data.status) throw new Error(data.description || data.message);

        document.dispatchEvent(
          new CustomEvent('cart:updated', { detail: { sections: data.sections } })
        );
        document.getElementById('cart-drawer')?.open(this.button);
      } catch (err) {
        if (this.errors) {
          this.errors.textContent = err.message;
          this.errors.hidden = false;
        }
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', busy);
      this.button.setAttribute('aria-busy', String(busy));
    }
  }
  customElements.define('cart-form', CartForm);

  /* ------------------------------------------------------------------ *
   * <cart-items> — quantity changes and removal
   * ------------------------------------------------------------------ */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) this.change(input.dataset.key, input.value);
      });

      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.key, 0);
        }
      });

      document.addEventListener('cart:updated', (e) => this.render(e.detail.sections));
    }

    async change(key, quantity) {
      this.classList.add('is-loading');
      try {
        const res = await fetch(routes.cart_change_url || '/cart/change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: Number(quantity),
            sections: 'cart-drawer,cart-page,header-cart-count',
          }),
        });
        const data = await res.json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { sections: data.sections } }));
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        const target = document.querySelector(`[data-section-render="${id}"]`);
        if (!target || !html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-section-render="${id}"]`);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });
    }
  }
  customElements.define('cart-items', CartItems);

  // Cart count in the header re-renders on every cart event
  document.addEventListener('cart:updated', (e) => {
    const sections = e.detail?.sections;
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      const target = document.querySelector(`[data-section-render="${id}"]`);
      if (!target || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`[data-section-render="${id}"]`);
      if (fresh) target.innerHTML = fresh.innerHTML;
    });
  });

  /* ------------------------------------------------------------------ *
   * <facet-form> — filter/sort without a full reload
   * ------------------------------------------------------------------ */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('change', debounce(() => this.apply(), 320));

      window.addEventListener('popstate', () => {
        window.location.reload();
      });
    }

    async apply() {
      const params = new URLSearchParams(new FormData(this.form)).toString();
      const url = `${window.location.pathname}?${params}`;

      const grid = document.querySelector('[data-section-render="product-grid"]');
      grid?.classList.add('is-loading');

      try {
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        ['product-grid', 'facets', 'active-facets', 'result-count'].forEach((id) => {
          const target = document.querySelector(`[data-section-render="${id}"]`);
          const fresh = doc.querySelector(`[data-section-render="${id}"]`);
          if (target && fresh) target.innerHTML = fresh.innerHTML;
        });

        window.history.pushState({}, '', url);
      } finally {
        grid?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ------------------------------------------------------------------ *
   * Sort <select> that submits its own form
   * ------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-sort-select]');
    if (select) select.form?.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();
